import { useApolloClient } from '@apollo/react-hooks'
import PortalLearningPaths from '@classes/LearningPaths'
import PortalStudentProfile from '@classes/StudentProfile'
import { LMSLearningPlans } from '@interfaces/LearningPath'
import { StoreProps } from '@interfaces/StoreState'
import { PROFILE_TYPE } from '@utils/constants'
import { setUserFlow } from '@utils/userflow'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import {
  GET_SKILLS_TUTOR_PRODUCTS,
  GET_STUDENT_LANGUAGE_MATERIALS,
  GET_MATERIALS_BY_LPIDS,
} from '@features/skillsTutor/api'
import { SkillsTutorProduct } from '@features/skillsTutor/types'
import { StudentLanguageMaterial } from '@interfaces/StudentLanguageMaterial'
import { pollForCondition } from '@features/skillsTutor/utils'
import { GET_USER_PROFILE } from '@queries/userProfile'
import { GetUserProfile } from '@interfaces/UserProfile'
import { fetchProfileSuccess } from '@redux/actions/userProfile'

// Constants
const SKILLS_TUTOR_DEFAULT_LANGUAGE = 'English'
const POLLING_CONFIG = { interval: 3000, timeout: 45000 }

export interface UseSkillsTutorDataResult {
  loading: boolean
  data: LMSLearningPlans[] | undefined
  error?: Error
  StudentProfile?: PortalStudentProfile
  skillsTutorProducts: SkillsTutorProduct[]
  LearningPaths: PortalLearningPaths
  skillsTutorMaterials: StudentLanguageMaterial[]
  skillsTutorMaterialsLoaded: boolean
  activeLearningLanguage?: string
  refresh: () => void
  refreshUntilConsistent: (params: {
    expectedAssignIds: string[]
    expectedUnassignIds: string[]
  }) => Promise<{ phase1Success: boolean; phase2Success: boolean; phase3Success: boolean }>
  isRefreshing: boolean
  refreshPhase: 0 | 1 | 2 | 3
}

/**
 * Custom hook for managing Skills Tutor data fetching, state management, and polling.
 * Consolidates: profile fetching, materials loading, product loading, learning paths loading,
 * and consistency polling after updates.
 */
export const useSkillsTutorData = (): UseSkillsTutorDataResult => {
  const client = useApolloClient()
  const dispatch = useDispatch()

  // Profile selection with stable reference
  const { profileInfo, isStudent } = useSelector(
    ({ userProfile }: StoreProps) => ({
      profileInfo: userProfile?.info?.StudentProfile?.StudentProfileInfo,
      isStudent: userProfile?.info?.Type === PROFILE_TYPE.STUDENT,
    }),
    shallowEqual
  )

  const studentProfile = useMemo<PortalStudentProfile | undefined>(
    () => (profileInfo ? new PortalStudentProfile(profileInfo) : undefined),
    [profileInfo]
  )

  // State management
  const [skillsTutorProducts, setSkillsTutorProducts] = useState<SkillsTutorProduct[]>([])
  const [studentLanguageMaterials, setStudentLanguageMaterials] = useState<StudentLanguageMaterial[]>([])
  const [skillsTutorMaterialsLoaded, setSkillsTutorMaterialsLoaded] = useState(false)
  const [learningPathsData, setLearningPathsData] = useState<LMSLearningPlans[] | undefined>(undefined)
  const [errors, setErrors] = useState<{ [key: string]: Error | undefined }>({})
  const [loading, setLoading] = useState(true)
  const [reloadToken, setReloadToken] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshPhase, setRefreshPhase] = useState<0 | 1 | 2 | 3>(0)

  const sfId = studentProfile?.SFId
  const activeLearningLanguage = SKILLS_TUTOR_DEFAULT_LANGUAGE

  // Helper: Fetch user profile
  const refreshUserProfile = useCallback(async (): Promise<boolean> => {
    if (!sfId) return false
    try {
      const res = await client.query<GetUserProfile>({
        query: GET_USER_PROFILE,
        variables: { Type: PROFILE_TYPE.STUDENT, Id: sfId },
        fetchPolicy: 'network-only',
      })
      if (res?.data?.getUserProfile) {
        dispatch(fetchProfileSuccess(res.data.getUserProfile))
        return true
      }
      return false
    } catch (err) {
      return false
    }
  }, [client, sfId, dispatch])

  // Helper: Fetch student language materials
  const fetchStudentLanguageMaterials = useCallback(async () => {
    if (!sfId) {
      setStudentLanguageMaterials([])
      setSkillsTutorMaterialsLoaded(true)
      return
    }
    try {
      const response = await client.query({
        query: GET_STUDENT_LANGUAGE_MATERIALS,
        fetchPolicy: 'no-cache',
        variables: { StudentProfileId: sfId, Language: activeLearningLanguage },
      })
      setStudentLanguageMaterials(response?.data?.getStudentLanguageMaterials || [])
      setErrors((prev) => ({ ...prev, slm: undefined }))
    } catch (err) {
      setErrors((prev) => ({ ...prev, slm: err as Error }))
    } finally {
      setSkillsTutorMaterialsLoaded(true)
    }
  }, [client, sfId, activeLearningLanguage])

  // Helper: Fetch skills tutor products
  const fetchSkillsTutorProducts = useCallback(async () => {
    if (!sfId) {
      setSkillsTutorProducts([])
      return
    }
    try {
      const response = await client.query({
        query: GET_SKILLS_TUTOR_PRODUCTS,
        fetchPolicy: 'no-cache',
      })
      setSkillsTutorProducts(response?.data?.getSkillsTutorProducts || [])
      setErrors((prev) => ({ ...prev, sp: undefined }))
    } catch (err) {
      setErrors((prev) => ({ ...prev, sp: err as Error }))
    }
  }, [client, sfId])

  // Helper: Fetch learning paths by material LPIDs
  const fetchLearningPaths = useCallback(async () => {
    if (!sfId || !skillsTutorMaterialsLoaded) return

    const assignedLPIDs = studentLanguageMaterials
      ?.filter((m) => m?.IsSkillsTutorMaterial && m?.Status === 'Assigned')
      .map((m) => m?.MaterialLPID)
      .filter(Boolean) as string[]

    if (!assignedLPIDs?.length) {
      setLearningPathsData(undefined)
      setLoading(false)
      return
    }

    try {
      const response = await client.query({
        query: GET_MATERIALS_BY_LPIDS,
        fetchPolicy: 'network-only',
        variables: { lpids: assignedLPIDs.join('|'), username: sfId },
      })
      const lpResp = response?.data?.getMaterialsByLpids
      setLearningPathsData(lpResp)
      setErrors((prev) => ({ ...prev, lp: undefined }))

      if (lpResp && studentProfile) {
        const LearningPaths = new PortalLearningPaths(lpResp, studentProfile, undefined, studentLanguageMaterials)
        const lp = LearningPaths.LearningPaths?.[0]
        setUserFlow(sfId, isStudent, lp)
      }
    } catch (err) {
      setErrors((prev) => ({ ...prev, lp: err as Error }))
    } finally {
      setLoading(false)
    }
  }, [client, sfId, studentProfile, isStudent, studentLanguageMaterials, skillsTutorMaterialsLoaded])

  // Main data fetching effect - triggered on mount and reloadToken change
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      setSkillsTutorMaterialsLoaded(false)
      setLearningPathsData(undefined)
      await Promise.all([fetchStudentLanguageMaterials(), fetchSkillsTutorProducts()])
    }
    loadData()
  }, [reloadToken, fetchStudentLanguageMaterials, fetchSkillsTutorProducts])

  // Learning paths effect - depends on materials being loaded
  useEffect(() => {
    fetchLearningPaths()
  }, [fetchLearningPaths])

  // Combined error state
  const error = errors.lp || errors.sp || errors.slm

  // Memoized LearningPaths instance
  const LearningPaths = useMemo(
    () => new PortalLearningPaths(learningPathsData ?? [], studentProfile, undefined, studentLanguageMaterials),
    [learningPathsData, studentProfile, studentLanguageMaterials]
  )

  // Simple refresh trigger
  const refresh = useCallback(() => {
    setLoading(true)
    setReloadToken((t) => t + 1)
  }, [])

  // Consolidated polling for consistency after updates
  const refreshUntilConsistent = useCallback(
    async ({
      expectedAssignIds,
      expectedUnassignIds,
    }: {
      expectedAssignIds: string[]
      expectedUnassignIds: string[]
    }) => {
      if (!sfId) {
        return { phase1Success: false, phase2Success: false, phase3Success: false }
      }

      setIsRefreshing(true)
      setRefreshPhase(1)

      try {
        // Phase 1: Poll for material updates
        const materialsResult = await pollForCondition<StudentLanguageMaterial[]>(
          async () => {
            const response = await client.query({
              query: GET_STUDENT_LANGUAGE_MATERIALS,
              fetchPolicy: 'no-cache',
              variables: { StudentProfileId: sfId, Language: activeLearningLanguage },
            })
            const materials = response?.data?.getStudentLanguageMaterials || []
            const skillsTutorMaterials = materials.filter((m: StudentLanguageMaterial) => m?.IsSkillsTutorMaterial)

            const allAssigned = expectedAssignIds.every((id) =>
              skillsTutorMaterials.some((m: StudentLanguageMaterial) => m.MaterialId === id && m.Status === 'Assigned')
            )
            const allUnassigned = expectedUnassignIds.every(
              (id) =>
                !skillsTutorMaterials.some(
                  (m: StudentLanguageMaterial) => m.MaterialId === id && m.Status === 'Assigned'
                )
            )

            return allAssigned && allUnassigned ? materials : null
          },
          POLLING_CONFIG
        )

        const phase1Success = materialsResult.success
        if (phase1Success && materialsResult.data) {
          setStudentLanguageMaterials(materialsResult.data)
        }

        // Phase 2: Poll for learning path updates (only if phase 1 succeeded)
        setRefreshPhase(2)
        const currentLPIDs = (materialsResult.data || [])
          .filter((m) => m?.IsSkillsTutorMaterial && m?.Status === 'Assigned')
          .map((m) => m?.MaterialLPID)
          .filter(Boolean) as string[]

        let phase2Success = false
        if (phase1Success && currentLPIDs.length > 0) {
          const lpResult = await pollForCondition<LMSLearningPlans[]>(
            async () => {
              const response = await client.query({
                query: GET_MATERIALS_BY_LPIDS,
                fetchPolicy: 'network-only',
                variables: { lpids: currentLPIDs.join('|'), username: sfId },
              })
              const lps = response?.data?.getMaterialsByLpids
              if (!Array.isArray(lps)) return null
              const returnedLpids = lps.map((lp: { lpid?: string }) => lp?.lpid).filter(Boolean)
              return currentLPIDs.every((id) => returnedLpids.includes(id)) ? lps : null
            },
            { interval: 2000, timeout: 30000 }
          )
          phase2Success = lpResult.success
          if (phase2Success && lpResult.data) {
            setLearningPathsData(lpResult.data)
          }
        } else {
          setLearningPathsData(undefined)
          phase2Success = phase1Success
        }

        // Phase 3: Refresh user profile
        setRefreshPhase(3)
        const phase3Success = phase1Success && phase2Success ? await refreshUserProfile() : false

        setRefreshPhase(0)
        setIsRefreshing(false)
        return { phase1Success, phase2Success, phase3Success }
      } catch (err) {
        setRefreshPhase(0)
        setIsRefreshing(false)
        return { phase1Success: false, phase2Success: false, phase3Success: false }
      }
    },
    [client, sfId, activeLearningLanguage, refreshUserProfile]
  )

  return {
    loading,
    data: learningPathsData,
    error,
    StudentProfile: studentProfile,
    skillsTutorProducts,
    LearningPaths,
    skillsTutorMaterials: studentLanguageMaterials,
    skillsTutorMaterialsLoaded,
    activeLearningLanguage,
    refresh,
    refreshUntilConsistent,
    isRefreshing,
    refreshPhase,
  }
}
